import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PageHeader from './PageHeader';
import {
  createCompany,
  createDepartment,
  createDesignation,
  createLocalBranch,
  fetchCompanies,
  fetchDepartments,
  fetchDesignations,
  listLocalBranches,
  updateCompany,
  updateDepartment,
  updateDesignation,
  updateLocalBranch,
} from '../utils/api';
import { formatDate } from '../utils/dateUtils';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import {
  pageFilterFormControlSx,
  pageFiltersActionsSx,
  pageFiltersControlsSx,
  pageFiltersToolbarSx,
} from '../utils/pageFilterStyles';
import {
  ORG_NAME_HELPER_TEXT,
  ORG_NAME_MAX_LENGTH,
  validateOrgDisplayName,
} from '../utils/orgNameValidation';

const PRIMARY = '#40518A';

const selectMenuProps = {
  PaperProps: {
    sx: { maxHeight: 320 },
  },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
};

const tablePaperSx = {
  width: '100%',
  overflow: 'hidden',
};

const tableContainerSx = {
  width: '100%',
  maxHeight: { xs: 'none', md: 'calc(100vh - 320px)' },
  overflowX: 'auto',
};

const tableSx = {
  minWidth: 960,
  '& .MuiTableCell-head': {
    fontWeight: 600,
    whiteSpace: 'nowrap',
    bgcolor: 'grey.50',
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
};

const multilineCellSx = {
  maxWidth: 280,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
};

const emptyCompanyForm = {
  company_name: '',
  address: '',
};

const emptyBranchForm = {
  branch_id: '',
  company_id: '',
  branch_name: '',
  branch_address: '',
};

const orgNameFieldProps = {
  inputProps: { maxLength: ORG_NAME_MAX_LENGTH },
  helperText: ORG_NAME_HELPER_TEXT,
};

const emptyMasterForm = {
  company_id: '',
  branch_id: '',
  name: '',
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const actorLabel = (name, raw) => {
  const resolved = (name || '').trim();
  if (resolved) return resolved;
  const fallback = (raw || '').trim();
  if (!fallback || UUID_RE.test(fallback)) return '';
  return fallback;
};

const AuditCell = ({ name, raw, at, requireActorForTime = false }) => {
  const actor = actorLabel(name, raw);
  const when = formatDate(at);
  const hasWhen = Boolean(when && when !== '-');
  const showTime = hasWhen && (!requireActorForTime || Boolean(actor));
  if (!actor && !showTime) {
    return <span style={{ color: '#999' }}>—</span>;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 120 }}>
      <Typography
        variant="body2"
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {actor || '—'}
      </Typography>
      {showTime && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {when}
        </Typography>
      )}
    </Box>
  );
};

const CompanyConfiguration = () => {
  const { confirm } = useConfirmDialog();
  const [tab, setTab] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [branchCompanyFilter, setBranchCompanyFilter] = useState('');
  const [masterCompanyId, setMasterCompanyId] = useState('');
  const [masterBranchId, setMasterBranchId] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [companyForm, setCompanyForm] = useState(emptyCompanyForm);
  const [branchForm, setBranchForm] = useState(emptyBranchForm);
  const [masterForm, setMasterForm] = useState(emptyMasterForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadCompanies = useCallback(async () => {
    try {
      const data = await fetchCompanies(200, 0, false, true);
      setCompanies(Array.isArray(data?.companies) ? data.companies : []);
    } catch (err) {
      showSnackbar(err?.message || 'Failed to load companies', 'error');
      setCompanies([]);
    }
  }, []);

  const loadBranches = useCallback(async (companyId = null) => {
    try {
      const data = await listLocalBranches({
        companyId: companyId || null,
        limit: 500,
      });
      setBranches(Array.isArray(data?.branches) ? data.branches : []);
    } catch (err) {
      showSnackbar(err?.message || 'Failed to load branches', 'error');
      setBranches([]);
    }
  }, []);

  const loadDesignations = useCallback(async (companyId, branchId) => {
    try {
      const data = await fetchDesignations({
        companyId: companyId || null,
        branchId: branchId || null,
        limit: 500,
      });
      setDesignations(Array.isArray(data?.designations) ? data.designations : []);
    } catch (err) {
      showSnackbar(err?.message || 'Failed to load designations', 'error');
      setDesignations([]);
    }
  }, []);

  const loadDepartments = useCallback(async (companyId, branchId) => {
    try {
      const data = await fetchDepartments({
        companyId: companyId || null,
        branchId: branchId || null,
        limit: 500,
      });
      setDepartments(Array.isArray(data?.departments) ? data.departments : []);
    } catch (err) {
      showSnackbar(err?.message || 'Failed to load departments', 'error');
      setDepartments([]);
    }
  }, []);

  const refreshCurrentTab = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 0) {
        await loadCompanies();
      } else if (tab === 1) {
        await Promise.all([loadCompanies(), loadBranches(branchCompanyFilter || null)]);
      } else {
        await Promise.all([loadCompanies(), loadBranches(null)]);
        if (tab === 2) {
          await loadDesignations(masterCompanyId, masterBranchId);
        } else {
          await loadDepartments(masterCompanyId, masterBranchId);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [
    tab,
    branchCompanyFilter,
    masterCompanyId,
    masterBranchId,
    loadCompanies,
    loadBranches,
    loadDesignations,
    loadDepartments,
  ]);

  useEffect(() => {
    refreshCurrentTab();
  }, [refreshCurrentTab]);

  const masterBranches = useMemo(
    () => branches.filter((branch) => !masterCompanyId || branch.company_id === masterCompanyId),
    [branches, masterCompanyId],
  );

  const formatBranchFilterLabel = useCallback((branch) => {
    const name = (branch.branch_name || '').trim() || branch.branch_id;
    if (!masterCompanyId && branch.company_name) {
      return `${name} — ${branch.company_name}`;
    }
    return name;
  }, [masterCompanyId]);

  const renderFilterSelects = () => {
    if (tab === 1) {
      return (
        <FormControl size="small" sx={pageFilterFormControlSx(240)}>
          <InputLabel id="branch-filter-company">Filter by company</InputLabel>
          <Select
            labelId="branch-filter-company"
            label="Filter by company"
            value={branchCompanyFilter}
            onChange={(e) => setBranchCompanyFilter(e.target.value)}
            MenuProps={selectMenuProps}
          >
            <MenuItem value="">All companies</MenuItem>
            {companies.map((company) => (
              <MenuItem key={company.company_id} value={company.company_id}>
                {company.company_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    if (tab === 2 || tab === 3) {
      return (
        <>
          <FormControl size="small" sx={pageFilterFormControlSx(240)}>
            <InputLabel id="master-filter-company">Filter by company</InputLabel>
            <Select
              labelId="master-filter-company"
              label="Filter by company"
              value={masterCompanyId}
              onChange={(e) => {
                const nextCompanyId = e.target.value;
                setMasterCompanyId(nextCompanyId);
                if (
                  masterBranchId
                  && nextCompanyId
                  && !branches.some(
                    (b) => b.branch_id === masterBranchId && b.company_id === nextCompanyId,
                  )
                ) {
                  setMasterBranchId('');
                }
              }}
              MenuProps={selectMenuProps}
            >
              <MenuItem value="">All companies</MenuItem>
              {companies.map((company) => (
                <MenuItem key={company.company_id} value={company.company_id}>
                  {company.company_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={pageFilterFormControlSx(240)}>
            <InputLabel id="master-filter-branch">Filter by branch</InputLabel>
            <Select
              labelId="master-filter-branch"
              label="Filter by branch"
              value={masterBranchId}
              onChange={(e) => setMasterBranchId(e.target.value)}
              MenuProps={selectMenuProps}
            >
              <MenuItem value="">All branches</MenuItem>
              {masterBranches.map((branch) => (
                <MenuItem key={branch.branch_id} value={branch.branch_id}>
                  {formatBranchFilterLabel(branch)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      );
    }
    return null;
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditing(null);
    setCompanyForm(emptyCompanyForm);
    setBranchForm(emptyBranchForm);
    setMasterForm(emptyMasterForm);
  };

  const openCreate = () => {
    setEditing(null);
    if (tab === 0) {
      setCompanyForm(emptyCompanyForm);
    } else if (tab === 1) {
      setBranchForm({
        ...emptyBranchForm,
        company_id: branchCompanyFilter || '',
      });
    } else {
      setMasterForm({
        ...emptyMasterForm,
        company_id: masterCompanyId || '',
        branch_id: masterBranchId || '',
      });
    }
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    if (tab === 0) {
      setCompanyForm({
        company_name: row.company_name || '',
        address: row.address || '',
      });
    } else if (tab === 1) {
      setBranchForm({
        branch_id: row.branch_id || '',
        company_id: row.company_id || '',
        branch_name: row.branch_name || '',
        branch_address: row.branch_address || '',
      });
    } else {
      setMasterForm({
        company_id: row.company_id || '',
        branch_id: row.branch_id || '',
        name: row.name || '',
      });
    }
    setDialogOpen(true);
  };

  const confirmSave = async (isEdit, label, name) => {
    const action = isEdit ? 'update' : 'create';
    const titleAction = isEdit ? 'Edit' : 'Create';
    const pretty = label.charAt(0).toUpperCase() + label.slice(1);
    return confirm({
      title: `Confirm ${titleAction} ${pretty}`,
      message: name
        ? `Are you sure you want to ${action} ${label} "${name}"?`
        : `Are you sure you want to ${action} this ${label}?`,
      confirmLabel: isEdit ? 'Save' : 'Create',
      danger: false,
    });
  };

  const handleSaveCompany = async () => {
    const companyName = companyForm.company_name.trim();
    const nameError = validateOrgDisplayName(companyName, 'Company name');
    if (nameError) {
      showSnackbar(nameError, 'error');
      return;
    }
    const approved = await confirmSave(Boolean(editing), 'company', companyName);
    if (!approved) return;
    setSaving(true);
    try {
      const payload = {
        companyName,
        address: companyForm.address.trim(),
      };
      if (editing) {
        await updateCompany(editing.company_id, payload);
        showSnackbar('Company updated');
      } else {
        await createCompany(payload);
        showSnackbar('Company created');
      }
      closeDialog();
      await loadCompanies();
    } catch (err) {
      showSnackbar(err?.message || 'Failed to save company', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBranch = async () => {
    const branchName = branchForm.branch_name.trim();
    const companyId = branchForm.company_id.trim();
    const branchNameError = validateOrgDisplayName(branchName, 'Branch name');
    if (editing) {
      if (branchNameError) {
        showSnackbar(branchNameError, 'error');
        return;
      }
      const approved = await confirmSave(true, 'branch', branchName);
      if (!approved) return;
      setSaving(true);
      try {
        await updateLocalBranch(editing.branch_id, {
          branchName,
          branchAddress: branchForm.branch_address.trim(),
        });
        showSnackbar('Branch updated');
        closeDialog();
        await loadBranches(branchCompanyFilter || null);
      } catch (err) {
        showSnackbar(err?.message || 'Failed to update branch', 'error');
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!companyId) {
      showSnackbar('Company is required', 'error');
      return;
    }
    if (branchNameError) {
      showSnackbar(branchNameError, 'error');
      return;
    }
    const approved = await confirmSave(false, 'branch', branchName);
    if (!approved) return;
    const branchId = (
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`
    ).slice(0, 36);
    setSaving(true);
    try {
      await createLocalBranch({
        branchId,
        companyId,
        branchName,
        branchAddress: branchForm.branch_address.trim(),
      });
      showSnackbar('Branch created');
      closeDialog();
      await loadBranches(branchCompanyFilter || null);
    } catch (err) {
      showSnackbar(err?.message || 'Failed to create branch', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMaster = async (kind) => {
    const name = masterForm.name.trim();
    const companyId = masterForm.company_id.trim();
    const branchId = masterForm.branch_id.trim();
    const label = kind === 'designation' ? 'Designation name' : 'Department name';
    const nameError = validateOrgDisplayName(name, label);
    if (nameError) {
      showSnackbar(nameError, 'error');
      return;
    }
    if (!editing && (!companyId || !branchId)) {
      showSnackbar('Company and branch are required', 'error');
      return;
    }
    const approved = await confirmSave(Boolean(editing), kind, name);
    if (!approved) return;
    setSaving(true);
    try {
      if (kind === 'designation') {
        if (editing) {
          await updateDesignation(editing.designation_id, { name });
          showSnackbar('Designation updated');
        } else {
          await createDesignation({ companyId, branchId, name });
          showSnackbar('Designation created');
        }
        closeDialog();
        await loadDesignations(masterCompanyId, masterBranchId);
      } else {
        if (editing) {
          await updateDepartment(editing.department_id, { name });
          showSnackbar('Department updated');
        } else {
          await createDepartment({ companyId, branchId, name });
          showSnackbar('Department created');
        }
        closeDialog();
        await loadDepartments(masterCompanyId, masterBranchId);
      }
    } catch (err) {
      showSnackbar(err?.message || `Failed to save ${kind}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (tab === 0) return handleSaveCompany();
    if (tab === 1) return handleSaveBranch();
    return handleSaveMaster(tab === 2 ? 'designation' : 'department');
  };

  const dialogTitle = () => {
    const action = editing ? 'Edit' : 'Create';
    if (tab === 0) return `${action} Company`;
    if (tab === 1) return `${action} Branch`;
    if (tab === 2) return `${action} Designation`;
    return `${action} Department`;
  };

  const createLabel = tab === 0
    ? 'Create Company'
    : tab === 1
      ? 'Create Branch'
      : tab === 2
        ? 'Create Designation'
        : 'Create Department';

  const renderCompanyDialogFields = () => (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <TextField
        label="Company Name"
        value={companyForm.company_name}
        onChange={(e) => setCompanyForm((prev) => ({ ...prev, company_name: e.target.value }))}
        required
        fullWidth
        {...orgNameFieldProps}
      />
      <TextField
        label="Address"
        value={companyForm.address}
        onChange={(e) => setCompanyForm((prev) => ({ ...prev, address: e.target.value }))}
        fullWidth
        multiline
        minRows={2}
      />
    </Stack>
  );

  const renderBranchDialogFields = () => (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <FormControl fullWidth required disabled={Boolean(editing)}>
        <InputLabel id="company-config-branch-company">Company</InputLabel>
        <Select
          labelId="company-config-branch-company"
          label="Company"
          value={branchForm.company_id}
          onChange={(e) => setBranchForm((prev) => ({ ...prev, company_id: e.target.value }))}
        >
          {companies.length === 0 ? (
            <MenuItem value="" disabled>No companies found</MenuItem>
          ) : (
            companies.map((company) => (
              <MenuItem key={company.company_id} value={company.company_id}>
                {company.company_name}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
      <TextField
        label="Branch Name"
        value={branchForm.branch_name}
        onChange={(e) => setBranchForm((prev) => ({ ...prev, branch_name: e.target.value }))}
        required
        fullWidth
        {...orgNameFieldProps}
      />
      <TextField
        label="Branch Address"
        value={branchForm.branch_address}
        onChange={(e) => setBranchForm((prev) => ({ ...prev, branch_address: e.target.value }))}
        fullWidth
        multiline
        minRows={2}
      />
    </Stack>
  );

  const renderMasterDialogFields = () => (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <FormControl fullWidth required disabled={Boolean(editing)}>
        <InputLabel id="company-config-master-company">Company</InputLabel>
        <Select
          labelId="company-config-master-company"
          label="Company"
          value={masterForm.company_id}
          onChange={(e) => setMasterForm((prev) => ({
            ...prev,
            company_id: e.target.value,
            branch_id: '',
          }))}
        >
          {companies.map((company) => (
            <MenuItem key={company.company_id} value={company.company_id}>
              {company.company_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth required disabled={Boolean(editing) || !masterForm.company_id}>
        <InputLabel id="company-config-master-branch">Branch</InputLabel>
        <Select
          labelId="company-config-master-branch"
          label="Branch"
          value={masterForm.branch_id}
          onChange={(e) => setMasterForm((prev) => ({ ...prev, branch_id: e.target.value }))}
        >
          {branches
            .filter((branch) => branch.company_id === masterForm.company_id)
            .map((branch) => (
              <MenuItem key={branch.branch_id} value={branch.branch_id}>
                {branch.branch_name || branch.branch_id}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      <TextField
        label="Name"
        value={masterForm.name}
        onChange={(e) => setMasterForm((prev) => ({ ...prev, name: e.target.value }))}
        required
        fullWidth
        {...orgNameFieldProps}
      />
    </Stack>
  );

  const renderTable = () => {
    if (tab === 0) {
      return (
        <Table size="medium" stickyHeader sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Updated By</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">No companies found.</TableCell>
              </TableRow>
            ) : (
              companies.map((company) => (
                <TableRow key={company.company_id}>
                  <TableCell>{company.company_name || '—'}</TableCell>
                  <TableCell>{company.address || '—'}</TableCell>
                  <TableCell>
                    <AuditCell
                      name={company.created_by_name}
                      raw={company.created_by}
                      at={company.created_at}
                    />
                  </TableCell>
                  <TableCell>
                    <AuditCell
                      name={company.updated_by_name}
                      raw={company.updated_by}
                      at={company.updated_at}
                      requireActorForTime
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(company)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      );
    }

    if (tab === 1) {
      return (
        <Table size="medium" stickyHeader sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell>Branch Name</TableCell>
              <TableCell>Company</TableCell>
              <TableCell sx={multilineCellSx}>Branch Address</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Updated By</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {branches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">No branches found.</TableCell>
              </TableRow>
            ) : (
              branches.map((branch) => (
                <TableRow key={branch.branch_id}>
                  <TableCell sx={{ fontWeight: 500 }}>{branch.branch_name || '—'}</TableCell>
                  <TableCell>{branch.company_name || '—'}</TableCell>
                  <TableCell sx={multilineCellSx}>{branch.branch_address || '—'}</TableCell>
                  <TableCell>
                    <AuditCell
                      name={branch.created_by_name}
                      raw={branch.created_by}
                      at={branch.created_at}
                    />
                  </TableCell>
                  <TableCell>
                    <AuditCell
                      name={branch.updated_by_name}
                      raw={branch.updated_by}
                      at={branch.updated_at}
                      requireActorForTime
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(branch)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      );
    }

    const rows = tab === 2 ? designations : departments;
    const noRowsLabel = tab === 2 ? 'No designations found.' : 'No departments found.';

    return (
      <Table size="medium" stickyHeader sx={tableSx}>
        <TableHead>
          <TableRow>
            <TableCell>{tab === 2 ? 'Designation' : 'Department'}</TableCell>
            <TableCell>Company</TableCell>
            <TableCell>Branch</TableCell>
            <TableCell>Created By</TableCell>
            <TableCell>Updated By</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center">{noRowsLabel}</TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.designation_id || row.department_id}>
                <TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>
                <TableCell>{row.company_name || '—'}</TableCell>
                <TableCell>{row.branch_name || '—'}</TableCell>
                <TableCell>
                  <AuditCell
                    name={row.created_by_name}
                    raw={row.created_by}
                    at={row.created_at}
                  />
                </TableCell>
                <TableCell>
                  <AuditCell
                    name={row.updated_by_name}
                    raw={row.updated_by}
                    at={row.updated_at}
                    requireActorForTime
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(row)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <PageHeader
        title="Company Configuration"
        subtitle="Create and edit company, branch, designation, and department"
      />

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Company" />
        <Tab label="Branch" />
        <Tab label="Designation" />
        <Tab label="Department" />
      </Tabs>

      <Paper variant="outlined" sx={tablePaperSx}>
        {(tab === 1 || tab === 2 || tab === 3) && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', lg: 'center' }}
              justifyContent="space-between"
              sx={pageFiltersToolbarSx}
            >
              <Box sx={pageFiltersControlsSx}>{renderFilterSelects()}</Box>
              <Box sx={pageFiltersActionsSx}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreate}
                  sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#334272' } }}
                >
                  {createLabel}
                </Button>
              </Box>
            </Stack>
          </Box>
        )}

        {tab === 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#334272' } }}
            >
              {createLabel}
            </Button>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={tableContainerSx}>
            {renderTable()}
          </TableContainer>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{dialogTitle()}</DialogTitle>
        <DialogContent>
          {tab === 0 && renderCompanyDialogFields()}
          {tab === 1 && renderBranchDialogFields()}
          {(tab === 2 || tab === 3) && renderMasterDialogFields()}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} sx={{ bgcolor: PRIMARY }}>
            {saving ? 'Saving…' : editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CompanyConfiguration;
